<template>
	<AppModal v-if="isOpen" @hide-modal="close">
		<template #header> Nieuwe offerteaanvraag </template>

		<AppModalContent>
			<form id="create-application-form" @submit.prevent="create">
				<AppAutocomplete
					v-model="selectedLender"
					label="Voor welke geldverstrekker wil je een offertaanvraag maken?"
					empty-message="Geen geldverstrekkers gevonden"
					:open-on-focus="!selectedLender"
					:has-icon="true"
					required
					:options="lenderOptions"
					:option-loader="getLenderOption"
					autofocus
				/>
			</form>
		</AppModalContent>

		<template #footer>
			<AppButtonGroup>
				<!-- form attribute: https://stackoverflow.com/questions/7020659/submit-form-using-a-button-outside-the-form-tag -->
				<AppButton
					type="submit"
					variant="primary"
					form="create-application-form"
					:loading="status === 'loading'"
				>
					Aanmaken
				</AppButton>
				<AppButton
					variant="secondary"
					:disabled="status === 'loading'"
					@click="close"
				>
					Annuleren
				</AppButton>
			</AppButtonGroup>
		</template>
	</AppModal>
</template>

<script lang="ts" setup>
import { api } from "@/loanLinkApi";
import { useDossierStore } from "@/store/useDossierStore";
import { watchOnce } from "@vueuse/core";
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useProviderLenders } from "@/composables/useProviderLenders";
import { useDossierDataStore } from "@/store/useDossierDataStore";

const isOpen = defineModel<boolean>();
const close = () => (isOpen.value = false);
const router = useRouter();

const dossierData = useDossierDataStore();
const selectedLender = ref(dossierData.data.mortgage.lenderCode);
const { lenderOptions, getLenderOption } = useProviderLenders();

const onSuccess = async (
	dossierDisplayId: number,
	loanApplicationId: string,
) => {
	await router.push({
		name: "LoanApplicationEditPage",
		params: {
			dossierDisplayId,
			loanApplicationId,
		},
	});
};

const status = ref<"success" | "error" | "idle" | "loading">("idle");
const apiError = ref<Error | null>(null);

const create = async () => {
	const dossierStore = useDossierStore();
	const selectedDossier = dossierStore?.selectedDossier;
	if (!selectedDossier || !selectedLender.value) {
		return;
	}

	const dossierId = selectedDossier.id;

	status.value = "loading";

	await api.loanApplication
		.createApplication({
			dossierId,
			lenderCode: selectedLender.value,
		})
		.then(async (res) => {
			status.value = "success";
			const dossierDisplayId = selectedDossier.displayId;

			if (dossierDisplayId) {
				await onSuccess(dossierDisplayId, res.data.id);
			}
		})
		.catch((res) => {
			status.value = "error";
			apiError.value = res.error;

			watchOnce(selectedLender, () => {
				apiError.value = null;
				status.value = "idle";
			});
		});
};
</script>

<style scoped>
#select-lender {
	border: 1px solid var(--border);
	border-radius: var(--border-radius);
	padding: 8px 10px;
	display: block;
	width: 100%;

	margin-top: 4px;
}

label {
	margin-top: 8px;
	margin-bottom: 8px;
}

#content {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 150px;
}

#error-display {
	color: var(--color-fail);
	margin-top: 10px;
}

#footer {
	display: flex;
	gap: 8px;
	margin-top: 16px;
}
</style>
